package com.playoffpulse.frontend.routing

import com.playoffpulse.shared.model.TeamRef

enum class NhlChartRoute(
    val slug: String,
    val title: String,
) {
    BLOCKED_SHOTS_CONTEXT("blocked-shots-context", "Attempts vs. Blocks"),
    BLOCKED_SHOTS_PERCENT("blocked-shots-percent", "% Blocked");

    companion object {
        val default: NhlChartRoute = BLOCKED_SHOTS_CONTEXT
        private val deprecatedSlugs = setOf(
            "blocked-attempts-vs-dz-share",
            "blocked-attempts-per-dz-share",
        )

        fun fromSlug(slug: String?): NhlChartRoute? {
            val normalized = slug?.trim()?.lowercase()?.takeIf { it.isNotBlank() } ?: return null
            return entries.firstOrNull { it.slug == normalized }
        }

        fun isDeprecatedSlug(slug: String?): Boolean {
            val normalized = slug?.trim()?.lowercase()?.takeIf { it.isNotBlank() } ?: return false
            return normalized in deprecatedSlugs
        }
    }
}

sealed interface ParsedNhlTeamRoute {
    data class Valid(
        val teamId: String,
        val canonicalPath: String,
        val shouldCanonicalize: Boolean,
    ) : ParsedNhlTeamRoute

    data class Invalid(
        val requestedCode: String,
    ) : ParsedNhlTeamRoute

    data object Base : ParsedNhlTeamRoute

    data object Movement : ParsedNhlTeamRoute

    data object Standings : ParsedNhlTeamRoute

    data class Charts(
        val chart: NhlChartRoute,
        val canonicalPath: String,
        val shouldCanonicalize: Boolean,
    ) : ParsedNhlTeamRoute

    data object About : ParsedNhlTeamRoute

    data object Faq : ParsedNhlTeamRoute

    data object Other : ParsedNhlTeamRoute
}

object NhlTeamRoute {
    private const val LEAGUE_ROOT = "/hockey/nhl"
    private const val ABOUT_SLUG = "about"
    private const val FAQ_SLUG = "faq"
    private const val MOVEMENT_SLUG = "movement"
    private const val STANDINGS_SLUG = "standings"
    private const val CHARTS_SLUG = "charts"

    fun candidateTeamId(pathname: String): String? {
        val normalized = normalizePath(pathname)
        val parts = normalized.split("/").filter { it.isNotBlank() }
        if (parts.size != 3) return null
        if (parts[0] != "hockey" || parts[1] != "nhl") return null
        if (parts[2].equals(ABOUT_SLUG, ignoreCase = true)) return null
        if (parts[2].equals(FAQ_SLUG, ignoreCase = true)) return null
        if (parts[2].equals(MOVEMENT_SLUG, ignoreCase = true)) return null
        if (parts[2].equals(STANDINGS_SLUG, ignoreCase = true)) return null
        if (parts[2].equals(CHARTS_SLUG, ignoreCase = true)) return null
        return parts[2].uppercase()
    }

    fun canonicalPath(teamId: String): String = "$LEAGUE_ROOT/${teamId.lowercase()}"

    fun rootPath(): String = LEAGUE_ROOT

    fun aboutPath(): String = "$LEAGUE_ROOT/$ABOUT_SLUG"

    fun faqPath(): String = "$LEAGUE_ROOT/$FAQ_SLUG"

    fun movementPath(): String = "$LEAGUE_ROOT/$MOVEMENT_SLUG"

    fun standingsPath(): String = "$LEAGUE_ROOT/$STANDINGS_SLUG"

    fun chartsRootPath(): String = "$LEAGUE_ROOT/$CHARTS_SLUG"

    fun chartsPath(chart: NhlChartRoute = NhlChartRoute.default): String = "$LEAGUE_ROOT/$CHARTS_SLUG/${chart.slug}"

    fun parse(
        pathname: String,
        teams: List<TeamRef>,
    ): ParsedNhlTeamRoute = parse(
        pathname = pathname,
        validTeamIds = teams.map { it.id }.toSet(),
    )

    fun parse(
        pathname: String,
        validTeamIds: Set<String>,
    ): ParsedNhlTeamRoute {
        val normalized = normalizePath(pathname)
        if (normalized == "/" || normalized == LEAGUE_ROOT) return ParsedNhlTeamRoute.Base
        if (normalized.equals(aboutPath(), ignoreCase = true)) return ParsedNhlTeamRoute.About
        if (normalized.equals(faqPath(), ignoreCase = true)) return ParsedNhlTeamRoute.Faq
        if (normalized.equals(movementPath(), ignoreCase = true)) return ParsedNhlTeamRoute.Movement
        if (normalized.equals(standingsPath(), ignoreCase = true)) return ParsedNhlTeamRoute.Standings
        if (normalized.equals(chartsRootPath(), ignoreCase = true)) {
            return ParsedNhlTeamRoute.Charts(
                chart = NhlChartRoute.default,
                canonicalPath = chartsPath(NhlChartRoute.default),
                shouldCanonicalize = true,
            )
        }
        chartsRoute(normalized)?.let { return it }

        val candidate = candidateTeamId(normalized) ?: return ParsedNhlTeamRoute.Other
        val canonical = canonicalPath(candidate)

        return if (validTeamIds.any { it.equals(candidate, ignoreCase = true) }) {
            ParsedNhlTeamRoute.Valid(
                teamId = candidate,
                canonicalPath = canonical,
                shouldCanonicalize = normalized != canonical,
            )
        } else {
            ParsedNhlTeamRoute.Invalid(candidate.lowercase())
        }
    }

    private fun normalizePath(pathname: String): String {
        val trimmed = pathname.trim().ifBlank { "/" }
        return if (trimmed.length > 1) {
            trimmed.trimEnd('/')
        } else {
            trimmed
        }
    }

    private fun chartsRoute(normalizedPath: String): ParsedNhlTeamRoute.Charts? {
        val prefix = "${chartsRootPath()}/"
        if (!normalizedPath.startsWith(prefix, ignoreCase = true)) return null
        val slug = normalizedPath.substring(prefix.length)
        if (slug.contains("/")) return null
        if (NhlChartRoute.isDeprecatedSlug(slug)) {
            return ParsedNhlTeamRoute.Charts(
                chart = NhlChartRoute.default,
                canonicalPath = chartsPath(NhlChartRoute.default),
                shouldCanonicalize = true,
            )
        }
        val chart = NhlChartRoute.fromSlug(slug) ?: return ParsedNhlTeamRoute.Charts(
            chart = NhlChartRoute.default,
            canonicalPath = chartsPath(NhlChartRoute.default),
            shouldCanonicalize = true,
        )
        val canonical = chartsPath(chart)
        return ParsedNhlTeamRoute.Charts(
            chart = chart,
            canonicalPath = canonical,
            shouldCanonicalize = normalizedPath != canonical,
        )
    }
}
